import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RSVPForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    guestNames: '',
    attendingDay1: false,
    attendingDay2: false,
    numberOfGuests: 1,
    songSuggestion: '',
    dietaryRequirements: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (name, checked) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.fullName || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }

    if (!formData.attendingDay1 && !formData.attendingDay2) {
      toast.error('Please select at least one day you\'ll be attending');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/rsvp`, formData);
      
      if (response.data) {
        toast.success('RSVP submitted successfully! We can\'t wait to celebrate with you! 💕');
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          guestNames: '',
          attendingDay1: false,
          attendingDay2: false,
          numberOfGuests: 1,
          songSuggestion: '',
          dietaryRequirements: '',
        });
      }
    } catch (error) {
      console.error('RSVP submission error:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit RSVP. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="vintage-card rounded-lg max-w-2xl mx-auto shadow-2xl">
      <div className="bg-gradient-to-r from-lightskyblue-600 to-lightskyblue-500 text-white rounded-t-lg p-8 border-b-4 border-gold-400">
        <div className="text-center">
          <div className="text-4xl mb-4">✦</div>
          <h3 className="font-serif text-4xl text-center mb-2">RSVP</h3>
          <div className="w-24 h-1 bg-gold-400 mx-auto mb-4"></div>
          <p className="text-center text-lightskyblue-50 font-body italic">
            Please let us know if you'll be joining us
          </p>
        </div>
      </div>
      <div className="p-8">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-lightskyblue-800 font-serif text-lg">
              Full Name *
            </Label>
            <Input
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              className="border-2 border-lightskyblue-300 focus:border-gold-400 focus:ring-gold-400 rounded-md font-body"
              required
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-lightskyblue-800 font-serif text-lg">
              Email Address *
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="your.email@example.com"
              className="border-2 border-lightskyblue-300 focus:border-gold-400 focus:ring-gold-400 rounded-md font-body"
              required
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-lightskyblue-800 font-serif text-lg">
              Phone Number
            </Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder="+44 7XXX XXXXXX"
              className="border-2 border-lightskyblue-300 focus:border-gold-400 focus:ring-gold-400 rounded-md font-body"
            />
          </div>

          {/* Guest Names */}
          <div className="space-y-2">
            <Label htmlFor="guestNames" className="text-lightskyblue-800 font-serif text-lg">
              Names of Guests
            </Label>
            <textarea
              id="guestNames"
              name="guestNames"
              value={formData.guestNames}
              onChange={handleInputChange}
              placeholder="Please list the names of all guests attending (one per line)"
              className="w-full min-h-20 px-3 py-2 border-2 border-lightskyblue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 resize-none font-body"
            />
            <p className="text-xs text-lightskyblue-600 italic">Include yourself and any additional guests</p>
          </div>

          {/* Attendance Days */}
          <div className="space-y-4 bg-lightskyblue-50 p-6 rounded-lg border-2 border-gold-400/30">
            <Label className="text-lightskyblue-800 font-serif text-lg block">
              Which day(s) will you be attending? *
            </Label>
            
            <div className="flex items-start space-x-3 p-4 bg-white rounded-md border border-lightskyblue-200">
              <Checkbox
                id="attendingDay1"
                checked={formData.attendingDay1}
                onCheckedChange={(checked) => handleCheckboxChange('attendingDay1', checked)}
                className="border-2 border-lightskyblue-400 data-[state=checked]:bg-lightskyblue-600 data-[state=checked]:border-lightskyblue-600 mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="attendingDay1"
                  className="text-lightskyblue-800 cursor-pointer font-normal"
                >
                  <span className="font-serif text-lg font-semibold">Day 1 - Saturday, 22nd August</span>
                  <p className="text-sm text-lightskyblue-600 mt-1 font-body italic">
                    Wedding ceremony at 13:00, followed by dinner, drinks, and dancing
                  </p>
                </Label>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-4 bg-white rounded-md border border-lightskyblue-200">
              <Checkbox
                id="attendingDay2"
                checked={formData.attendingDay2}
                onCheckedChange={(checked) => handleCheckboxChange('attendingDay2', checked)}
                className="border-2 border-lightskyblue-400 data-[state=checked]:bg-lightskyblue-600 data-[state=checked]:border-lightskyblue-600 mt-1"
              />
              <div className="flex-1">
                <Label
                  htmlFor="attendingDay2"
                  className="text-lightskyblue-800 cursor-pointer font-normal"
                >
                  <span className="font-serif text-lg font-semibold">Day 2 - Sunday, 23rd August</span>
                  <p className="text-sm text-lightskyblue-600 mt-1 font-body italic">
                    Casual celebration with laid-back drinks, food, and music
                  </p>
                </Label>
              </div>
            </div>
          </div>

          {/* Number of Guests */}
          <div className="space-y-2">
            <Label htmlFor="numberOfGuests" className="text-lightskyblue-800 font-serif text-lg">
              Number of Guests (including yourself)
            </Label>
            <Input
              id="numberOfGuests"
              name="numberOfGuests"
              type="number"
              min="1"
              max="10"
              value={formData.numberOfGuests}
              onChange={handleInputChange}
              className="border-2 border-lightskyblue-300 focus:border-gold-400 focus:ring-gold-400 rounded-md font-body"
            />
          </div>

          {/* Dietary Requirements */}
          <div className="space-y-2">
            <Label htmlFor="dietaryRequirements" className="text-lightskyblue-800 font-serif text-lg">
              Dietary Requirements or Special Requests
            </Label>
            <textarea
              id="dietaryRequirements"
              name="dietaryRequirements"
              value={formData.dietaryRequirements}
              onChange={handleInputChange}
              placeholder="Please let us know of any dietary requirements or special requests..."
              className="w-full min-h-24 px-3 py-2 border-2 border-lightskyblue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-400 focus:border-gold-400 resize-none font-body"
            />
          </div>

          {/* Song Suggestion */}
          <div className="space-y-2">
            <Label htmlFor="songSuggestion" className="text-lightskyblue-800 font-serif text-lg">
              Song Suggestion
            </Label>
            <Input
              id="songSuggestion"
              name="songSuggestion"
              value={formData.songSuggestion}
              onChange={handleInputChange}
              placeholder="Suggest a song for the celebration!"
              className="border-2 border-lightskyblue-300 focus:border-gold-400 focus:ring-gold-400 rounded-md font-body"
            />
            <p className="text-xs text-lightskyblue-600 italic">Help us create the perfect playlist</p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-lightskyblue-600 to-lightskyblue-500 hover:from-lightskyblue-700 hover:to-lightskyblue-600 text-white py-6 text-lg font-serif border-2 border-gold-400 shadow-lg transition-all duration-200 hover:shadow-xl"
          >
            {isSubmitting ? 'Submitting...' : 'Submit RSVP'}
          </Button>

          <p className="text-center text-sm text-lightskyblue-600 mt-4 font-body italic">
            * Required fields
          </p>
        </form>
      </div>
    </div>
  );
};

export default RSVPForm;